/**
 * "Sign in first" — and then do what was asked (the user, 2026-10-26: "the app will not
 * require a login at the opening … when the user wants to save a set it will require a
 * login, then the login page will be open — same for the ride boards and everything else").
 *
 * Browsing a rave needs no account; anything that is somebody's own does. A screen that
 * needs one calls `askToSignIn`: the page is remembered (`rememberReturn` — the sign-in
 * comes back to it) and the login page opens. A SET TO SAVE rides in that same address as
 * `#save=<slug>` — no cookie of its own (the privacy policy names every cookie the app
 * sets). Back on the rave, `takePendingSave` hands the slug over ONCE and the screen saves
 * it. The address is read as this module loads, before any screen rewrites the hash.
 *
 * Only a save is replayed: every other action ("open the Rideboard", "my lineup") is a
 * place, and coming back to the page is the action.
 */
import { loginUrl } from './tenant';
import { rememberReturn } from '@/app/ReturnAfterSignIn';

const KEY = 'save';

/** The slug a page was opened with (`#save=<slug>`), read once as the page loads. */
let arrivedWith: string | null = readSave(typeof location === 'undefined' ? '' : location.hash);

function readSave(hash: string): string | null {
  const value = new URLSearchParams(hash.replace(/^#/, '')).get(KEY);
  return value && value.length <= 200 ? value : null;
}

/** This page's address with `#save=<slug>` — the rest of the hash dropped (it is the save's page). */
export function withSave(href: string, slug: string): string {
  const u = new URL(href);
  u.hash = `${KEY}=${encodeURIComponent(slug)}`;
  return u.toString();
}

/** An address without its `#save=…` — "Not now" goes back without saving anything. */
export function withoutSave(href: string): string {
  try {
    const u = new URL(href);
    if (readSave(u.hash) !== null) u.hash = '';
    return u.toString();
  } catch {
    return href;
  }
}

/**
 * Leave for the sign-in, remembering where we were — and the set to save (`slug`), or a hash
 * to come back to (`hash`, e.g. a shared lineup's `plan=…`, so its dialog offers it again).
 */
export function askToSignIn(then?: { slug?: string; hash?: string; to?: string }): void {
  let href = then?.to ?? window.location.href;
  if (then?.slug) href = withSave(href, then.slug);
  else if (then?.hash) { const u = new URL(href); u.hash = then.hash; href = u.toString(); }
  // `to`: come back somewhere else than this page — See all raves, `/raves?all=1` (2026-10-26).
  rememberReturn(then?.to ? new URL(then.to).pathname : window.location.pathname, href);
  window.location.assign(loginUrl());
}

/** The set this page was opened to save — taken once — or null. Clears it from the address. */
export function takePendingSave(): string | null {
  const slug = arrivedWith;
  arrivedWith = null;
  if (slug !== null && typeof history !== 'undefined' && readSave(location.hash) !== null) {
    history.replaceState(history.state, '', `${location.pathname}${location.search}`);
  }
  return slug;
}

/** Whether this page was opened to save a set — without taking it (the Attend screen attends by itself). */
export function hasPendingSave(): boolean {
  return arrivedWith !== null;
}

/** For tests: as if the page had just been opened at `hash`. */
export function arriveForTests(hash: string): void {
  arrivedWith = readSave(hash);
}

/*
 * "Sign in first?" — ASKED before leaving (the user, 2026-10-27: "tapping a heart should pop up a
 * message to the user — to save sets you must sign in — with 2 buttons, sign in or cancel"). A screen
 * calls `askBeforeSignIn`; the shell's `SignInAsk` (app/SignInAsk.tsx) shows the question, and its
 * Sign in is `askToSignIn` with the same `then`. Cancel leaves everything as it was.
 */
type Asked = Parameters<typeof askToSignIn>[0];
/**
 * What the question is about — a set to save (the default), an artist to follow (step 76), or the
 * Rideboard (the 0.14.82 walk, 2026-11-02: the board is READ by anybody; posting or answering asks
 * `'sign-in'` signed out, `'register'` for an account whose registration is not complete).
 */
export type AskedFor = { follow?: string; rides?: 'sign-in' | 'register' };
type Question = { then: Asked; follow?: string; rides?: 'sign-in' | 'register' };
let asked: Question | null = null;
const askListeners = new Set<() => void>();

/** Ask "sign in to do this?" — the shell's dialog answers. */
export function askBeforeSignIn(then?: Asked, about?: AskedFor): void {
  asked = { then, follow: about?.follow, rides: about?.rides };
  for (const listener of askListeners) listener();
}

/** The question open now, or null. */
export function signInAsked(): Question | null {
  return asked;
}

export function onSignInAsked(listener: () => void): () => void {
  askListeners.add(listener);
  return () => { askListeners.delete(listener); };
}

/** Close the question (Cancel, or on the way to the sign-in). */
export function closeSignInAsk(): void {
  asked = null;
  for (const listener of askListeners) listener();
}
